import { useEffect, useRef } from 'react';
import type { Map as MapboxMap } from 'mapbox-gl';
import type { EventWithCircuit } from '../../api/eventsService';
import { getRadarBboxCoordinates } from './radarLayerUtils';
import type { RadarTile } from './useDataFetching';
import {
  transitionToFrame,
  removeTransitionLayers,
  updateTransitionOpacity,
  createTransitionState,
  type TransitionLayerConfig,
  type TransitionState,
  type ImageCacheLike,
} from './radarTransition';

// ============================================================================
// Types
// ============================================================================

export interface UseRadarLayerParams {
  mapRef: React.RefObject<MapboxMap | null>;
  selectedEvent: EventWithCircuit | null;
  extendedTiles: RadarTile[];
  currentIndex: number;
  radarOpacity: number;
  fps: number;
  styleVersion: number;
  tilesSource: string;
  mapTileProvider: string;
  radarSourceId: string;
  radarLayerId: string;
  radarSourceId2: string;
  radarLayerId2: string;
  imageCacheRef: React.RefObject<ImageCacheLike>;
  /** When false the hook is a no-op (default: true). */
  enabled?: boolean;
  /**
   * When true, radar layers are hidden via visibility:none instead of removed.
   * Used by split/PiP maps when the TIO provider is active.
   */
  isTioActive?: boolean;
  /**
   * Override tile URL for the current frame. When provided, this URL is used
   * instead of `extendedTiles[currentIndex].url`. Split/PiP maps pass the
   * pre-colorized numericBlobUrl here so they don't need their own numeric
   * radar hook.
   */
  resolvedTileUrl?: string | null;
}

// ============================================================================
// Constants
// ============================================================================

const MAX_RETRIES = 20;
const RETRY_DELAY = 50;

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to manage radar tile layer on any Nowcasting map instance.
 *
 * Uses a shared decode-aware transition helper with two image sources/layers
 * (A/B double-buffering). The new frame is preloaded and decoded before
 * Mapbox is touched, then faded in on top of the old frame. The old frame
 * stays visible until the fade completes, preventing blank flashes.
 */
export function useRadarLayer({
  mapRef,
  selectedEvent,
  extendedTiles,
  currentIndex,
  radarOpacity,
  fps,
  styleVersion,
  tilesSource,
  mapTileProvider,
  radarSourceId,
  radarLayerId,
  radarSourceId2,
  radarLayerId2,
  imageCacheRef,
  enabled = true,
  isTioActive = false,
  resolvedTileUrl,
}: UseRadarLayerParams): void {
  const transitionStateRef = useRef<TransitionState>(createTransitionState());
  const configRef = useRef<TransitionLayerConfig>({
    sourceIdA: radarSourceId,
    layerIdA: radarLayerId,
    sourceIdB: radarSourceId2,
    layerIdB: radarLayerId2,
  });
  const lastTilesRef = useRef<RadarTile[] | null>(null);
  const prevTioActiveRef = useRef(isTioActive);

  // Keep config in sync if IDs change
  useEffect(() => {
    configRef.current = {
      sourceIdA: radarSourceId,
      layerIdA: radarLayerId,
      sourceIdB: radarSourceId2,
      layerIdB: radarLayerId2,
    };
  }, [radarSourceId, radarLayerId, radarSourceId2, radarLayerId2]);

  // Reset transition state when style reloads (layers are gone)
  useEffect(() => {
    transitionStateRef.current = createTransitionState();
  }, [styleVersion]);

  // Restore radar layer visibility when switching FROM TIO back to radar.
  // Kept separate from the per-frame effect so it doesn't trigger Mapbox
  // source reprocessing on every frame advance.
  useEffect(() => {
    const wasTio = prevTioActiveRef.current;
    prevTioActiveRef.current = isTioActive;
    if (wasTio && !isTioActive) {
      const map = mapRef.current;
      if (!map) return;
      try {
        if (map.getLayer(configRef.current.layerIdA)) {
          map.setLayoutProperty(configRef.current.layerIdA, 'visibility', 'visible');
        }
        if (map.getLayer(configRef.current.layerIdB)) {
          map.setLayoutProperty(configRef.current.layerIdB, 'visibility', 'visible');
        }
      } catch {
        // ignore
      }
    }
  }, [isTioActive, mapRef]);

  // Main frame update effect
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedEvent || !enabled) return;

    // When TIO is active, hide radar layers instead of removing them
    if (isTioActive) {
      try {
        if (map.getLayer(configRef.current.layerIdA)) {
          map.setLayoutProperty(configRef.current.layerIdA, 'visibility', 'none');
        }
        if (map.getLayer(configRef.current.layerIdB)) {
          map.setLayoutProperty(configRef.current.layerIdB, 'visibility', 'none');
        }
      } catch {
        // Ignore errors
      }
      return;
    }

    // On the main map, numeric rendering is handled by useNumericRadarLayer.
    // On split/PiP, the caller provides the pre-colorized blob via resolvedTileUrl.
    if (mapTileProvider === 'radar-l2-numeric' && !resolvedTileUrl) {
      removeTransitionLayers(map, configRef.current, transitionStateRef.current);
      return;
    }

    if (extendedTiles.length === 0) {
      removeTransitionLayers(map, configRef.current, transitionStateRef.current);
      lastTilesRef.current = extendedTiles;
      return;
    }

    const currentTile = extendedTiles[currentIndex];
    if (!currentTile) return;

    const tileListChanged = lastTilesRef.current !== extendedTiles;
    lastTilesRef.current = extendedTiles;
    if (tileListChanged && transitionStateRef.current.initialized) {
      // A freshly fetched range can keep the same numeric index while pointing
      // at cold URLs. Treat it as a new baseline so the frame is not dropped by
      // the short playback-oriented source-load timeout.
      transitionStateRef.current.generation++;
      if (transitionStateRef.current.fadeRafId !== null) {
        cancelAnimationFrame(transitionStateRef.current.fadeRafId);
      }
      transitionStateRef.current = createTransitionState();
    }

    const tileUrl = resolvedTileUrl ?? currentTile.url;
    const coordinates = getRadarBboxCoordinates(selectedEvent as EventWithCircuit);

    let retryCount = 0;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const applyUpdate = () => {
      if (cancelled) return;
      try {
        if (!map.isStyleLoaded()) {
          retryCount++;
          if (retryCount < MAX_RETRIES) {
            timeoutId = setTimeout(applyUpdate, RETRY_DELAY);
          }
          return;
        }
      } catch {
        return;
      }

      transitionToFrame(
        map,
        configRef.current,
        transitionStateRef.current,
        tileUrl,
        coordinates,
        radarOpacity,
        fps,
        imageCacheRef.current,
      );
    };

    applyUpdate();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [currentIndex, extendedTiles, selectedEvent, radarSourceId, radarLayerId, radarSourceId2, radarLayerId2, styleVersion, tilesSource, mapRef, radarOpacity, fps, imageCacheRef, mapTileProvider, enabled, isTioActive, resolvedTileUrl]);

  // Separate opacity effect for slider changes
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !enabled) return;

    updateTransitionOpacity(
      map,
      configRef.current,
      transitionStateRef.current,
      radarOpacity,
    );
  }, [radarOpacity, radarLayerId, mapRef, enabled]);
}
